/**
 * Time-off API hooks — TanStack Query layer over /api/time-off.
 *
 * State machine: Pending → Approved | Denied
 *   - Employees create requests for themselves
 *   - Admin/Manager approve/deny (cannot approve their own — separation of duties)
 *
 * The decide mutation applies an optimistic update so the row flips status
 * immediately, then rolls back on error.
 *
 * PTO balances are tracked in Workforce, not in this app.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api.js';
import type { TimeOffRequest, Paginated } from '@shared/types';
import { timeOffCreateInput, timeOffUpdateInput } from '@shared/schemas/index.js';
import { z } from 'zod';

export type TimeOffCreateBody = z.input<typeof timeOffCreateInput>;
export type TimeOffUpdateBody = z.input<typeof timeOffUpdateInput>;
export type TimeOffDecision = 'Approved' | 'Denied';

export const timeOffKeys = {
  all: ['timeoff'] as const,
  list: (params?: { employeeId?: string }) =>
    [...timeOffKeys.all, 'list', params ?? {}] as const,
  calendar: () => [...timeOffKeys.all, 'calendar'] as const,
};

/** List time-off requests, optionally scoped to an employee. */
export function useTimeOff(params?: { employeeId?: string }) {
  return useQuery({
    queryKey: timeOffKeys.list(params),
    queryFn: () =>
      api.get<Paginated<TimeOffRequest>>(`/api/time-off${qs(params)}`),
  });
}

/** Company leave calendar — Approved for everyone; Pending included for Admins. */
export function useTimeOffCalendar() {
  return useQuery({
    queryKey: timeOffKeys.calendar(),
    queryFn: () =>
      api.get<{ items: TimeOffRequest[]; includePending: boolean }>(
        '/api/time-off/calendar',
      ),
  });
}

/** Create a time-off request (employees may only request for themselves). */
export function useCreateTimeOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TimeOffCreateBody) =>
      api.post<TimeOffRequest>('/api/time-off', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timeOffKeys.all });
    },
  });
}

/**
 * Approve or deny a pending time-off request (Admin/Manager).
 * Optimistically patches every cached list so the UI flips immediately and
 * rolls back if the server rejects the decision.
 */
export function useDecideTimeOff() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: TimeOffDecision }) =>
      api.post<TimeOffRequest>(`/api/time-off/${id}/decide`, { status }),

    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: timeOffKeys.all });

      const previousLists = queryClient.getQueriesData<Paginated<TimeOffRequest>>({
        queryKey: timeOffKeys.all,
      });

      queryClient.setQueriesData<Paginated<TimeOffRequest>>(
        { queryKey: timeOffKeys.all },
        (cached) => {
          if (!cached?.items) return cached;
          return {
            ...cached,
            items: cached.items.map((req) =>
              req.id === id
                ? { ...req, status, decidedAt: new Date().toISOString() }
                : req,
            ),
          };
        },
      );

      return { previousLists };
    },

    onError: (_error, _vars, context) => {
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: timeOffKeys.all });
    },
  });
}

/** Update a pending time-off request (owner or Admin/Manager). */
export function useUpdateTimeOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TimeOffUpdateBody }) =>
      api.put<TimeOffRequest>(`/api/time-off/${id}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timeOffKeys.all });
    },
  });
}

/** Delete a time-off request (owner/Admin/Manager for Pending; Admin for decided). */
export function useDeleteTimeOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`/api/time-off/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timeOffKeys.all });
    },
  });
}
